import { GoogleGenAI, Type, Schema } from "@google/genai";
import { PDiagramData, FmeaItem, Control, DvprTest, AiInsight, DvprItem, AltDataPoint, FmeaStandard } from "../types";
import { authenticatedFetchJSON } from "../utils/authenticatedFetch";
import { FMEA_SCHEMAS, getFmeaGenerationResponseSchema } from "./geminiSchemas";
import { getFmeaGenerationPrompt } from "./geminiPrompts";

import { getEnv } from "../utils/env";

const apiKey = getEnv('VITE_GEMINI_API_KEY') || getEnv('GEMINI_API_KEY') || getEnv('API_KEY');

// Initialize client only if key exists
const ai = apiKey ? new GoogleGenAI({ apiKey }) : null;

const noiseFactorSchema: Schema = {
  type: Type.OBJECT,
  properties: {
    pieceToPiece: {
      type: Type.ARRAY,
      items: { type: Type.STRING },
      description: "Variations in manufacturing, tolerances, material differences."
    },
    changeOverTime: {
      type: Type.ARRAY,
      items: { type: Type.STRING },
      description: "Wear, aging, corrosion, degradation over lifespan."
    },
    customerUsage: {
      type: Type.ARRAY,
      items: { type: Type.STRING },
      description: "How the customer uses, misuses, or maintains the system."
    },
    externalEnvironment: {
      type: Type.ARRAY,
      items: { type: Type.STRING },
      description: "Temperature, humidity, road conditions, external vibration."
    },
    internal: {
      type: Type.ARRAY,
      items: { type: Type.STRING },
      description: "Interactions with neighboring subsystems or internal friction."
    }
  },
  required: ["pieceToPiece", "changeOverTime", "customerUsage", "externalEnvironment", "internal"]
};

const pDiagramSchema: Schema = {
  type: Type.OBJECT,
  properties: {
    systemName: { type: Type.STRING, description: "A concise name for the system being analyzed." },
    consolidatedFunctionId: { type: Type.STRING, description: "A unique identifier or function name for the system." },
    goal: { type: Type.ARRAY, items: { type: Type.STRING }, description: "The primary objectives or goals of the system design." },
    inputSignals: { type: Type.ARRAY, items: { type: Type.STRING }, description: "Inputs required for the system to function (Energy, Information, Material)." },
    controlFactors: { type: Type.ARRAY, items: { type: Type.STRING }, description: "Parameters the engineer can change or control (Design choices)." },
    systemFunction: { type: Type.ARRAY, items: { type: Type.STRING }, description: "What the system actually does (Energy transformation)." },
    expectedOutput: { type: Type.ARRAY, items: { type: Type.STRING }, description: "Desired results or ideal functions." },
    unexpectedOutput: { type: Type.ARRAY, items: { type: Type.STRING }, description: "Error states, failure modes, or negative side effects." },
    noiseFactors: noiseFactorSchema
  },
  required: ["systemName", "consolidatedFunctionId", "goal", "inputSignals", "controlFactors", "systemFunction", "expectedOutput", "unexpectedOutput", "noiseFactors"]
};

// Helper to clean markdown from JSON
const cleanJson = (text: string): any => {
  let cleanText = text.trim();
  if (cleanText.startsWith('```json')) cleanText = cleanText.slice(7);
  if (cleanText.startsWith('```')) cleanText = cleanText.slice(3);
  if (cleanText.endsWith('```')) cleanText = cleanText.slice(0, -3);
  return JSON.parse(cleanText.trim());
};

// Helper: Generate P-Diagram via Backend
const generatePDiagramBackend = async (
  systemDescription: string,
  projectName: string,
  epochId: number
): Promise<PDiagramData> => {
  try {
    const pDiagram = await authenticatedFetchJSON('/api/agents/p-diagram/generate', {
      method: 'POST',
      body: JSON.stringify({
        system_description: systemDescription,
        project_name: projectName,
        epoch_id: epochId
      })
    });

    if (!pDiagram.projectName) pDiagram.projectName = projectName;
    if (!pDiagram.epochId) pDiagram.epochId = epochId;
    if (!pDiagram.status) pDiagram.status = 'draft';

    return pDiagram;
  } catch (e) {
    console.error("Backend Error, falling back to direct if possible or throwing", e);
    throw e;
  }
};

// Helper: Generate P-Diagram via Direct AI
const generatePDiagramDirect = async (
  systemDescription: string,
  projectName: string,
  epochId: number
): Promise<PDiagramData> => {
  if (!ai || !apiKey) {
    throw new Error("Gemini API Key is missing. Please add VITE_GEMINI_API_KEY to your .env file.");
  }

  try {
    const prompt = `
      You are an expert Systems Engineer specializing in Quality Function Deployment and Robust Design.
      Create a comprehensive P-Diagram (Parameter Diagram) based on the following system description.
      
      System Description: "${systemDescription}"
      
      Analyze the system to identify:
      1. Inputs (Signal factors)
      2. Control Factors (Design parameters)
      3. System Functions (The transformation)
      4. Ideal/Expected Outputs
      5. Error States/Unexpected Outputs
      6. Noise Factors (Categorized strictly into the 5 standard categories)
      
      Also provide a 'consolidatedFunctionId' which is a short, unique, slug-like identifier for this function (e.g., 'cooling-system-v1').
      
      Be technical, precise, and concise.
    `;

    const response = await ai.models.generateContent({
      model: "gemini-3-flash-preview",
      contents: [{ role: "user", parts: [{ text: prompt }] }],
      config: {
        responseMimeType: "application/json",
        responseSchema: pDiagramSchema,
        temperature: 0.3,
      },
    });

    const text = response.text;

    if (!text) {
      throw new Error("No response received from AI model.");
    }

    const data = cleanJson(text);

    return {
      ...data,
      title: data.systemName || data.title || "Generated P-Diagram",
      projectName: projectName,
      epochId: epochId,
      status: 'draft'
    } as PDiagramData;

  } catch (error: any) {
    console.error("Gemini API Error:", error);
    if (error.message && error.message.includes("404")) {
      throw new Error("Model not found. Please ensure your API Key has access to 'gemini-3-flash-preview'.");
    }
    throw new Error(`Failed to generate P-Diagram: ${error.message || error}`);
  }
};

export const generatePDiagram = async (
  systemDescription: string,
  options?: {
    apiMode?: 'direct' | 'backend',
    projectName?: string,
    epochId?: number
  }
): Promise<PDiagramData> => {
  const mode = options?.apiMode || 'backend'; // Force backend default per user request
  const projectName = options?.projectName || 'Unknown Project';
  const epochId = options?.epochId || 1;

  if (mode === 'backend') {
    return generatePDiagramBackend(systemDescription, projectName, epochId);
  }

  return generatePDiagramDirect(systemDescription, projectName, epochId);
};


export const chatWithAI = async (query: string, contextData: any): Promise<string> => {
  try {
    // ✅ SECURITY: Always use backend for chat to protect API keys and enforce RBAC
    const response = await authenticatedFetchJSON<string>('/api/agents/chat', {
      method: 'POST',
      body: JSON.stringify({
        query,
        context: contextData
      })
    });

    return response;
  } catch (error: any) {
    console.error("Chat Error:", error);

    // Fallback logic for development only if absolutely needed, 
    // but preferred to throw to enforce secure architecture.
    if (getEnv('VITE_DEV_ALLOW_DIRECT_AI') === 'true' && ai && apiKey) {
      console.warn("⚠️ Falling back to direct AI (Not Secure)");
      return await directChatFallback(query, contextData);
    }

    return `Error: ${error.message}`;
  }
};

/** Internal fallback for development only */
const directChatFallback = async (query: string, contextData: any): Promise<string> => {
  if (!ai || !apiKey) return "API Key Missing";

  // (Original logic from before, but kept in separate function for clarity)
  const contextString = JSON.stringify(contextData, null, 2);
  const prompt = `General Analysis query: ${query}\n\nContext: ${contextString}`;

  const response = await ai.models.generateContent({
    model: "gemini-3-flash-preview",
    contents: [{ role: "user", parts: [{ text: prompt }] }],
    config: { temperature: 0.4 }
  });

  return response.text || "No response generated.";
};


/**
 * Get AI Workflow Coaching using Specialized Agents
 * This sends a focused prompt with compliance metrics to get agent-specific guidance
 */
export const getWorkflowCoaching = async (
  agentPrompt: string
): Promise<string> => {
  try {
    // ✅ SECURITY: Use backend for workflow coaching
    const response = await authenticatedFetchJSON<string>('/api/agents/workflow/coach', {
      method: 'POST',
      body: JSON.stringify({
        prompt: agentPrompt
      })
    });

    return response;
  } catch (error: any) {
    console.error("Workflow Coaching Error:", error);

    // Fallback logic
    if (getEnv('VITE_DEV_ALLOW_DIRECT_AI') === 'true' && ai && apiKey) {
      const response = await ai.models.generateContent({
        model: "gemini-3-flash-preview",
        contents: [{ role: "user", parts: [{ text: agentPrompt }] }],
        config: { temperature: 0.6 }
      });
      return response.text || "No coaching generated.";
    }

    return `Error getting workflow coaching: ${error.message}`;
  }
};


// Helper: Generate FMEA Drafts via Backend
const generateFmeaDraftsBackend = async (
  payload: any,
  projectName: string,
  epochId: number
): Promise<{ markdownReport: string, newFmeaItems: Partial<FmeaItem>[] }> => {
  try {
    const result = await authenticatedFetchJSON('/api/agents/fmea/generate-drafts', {
      method: 'POST',
      body: JSON.stringify(payload)
    });

    if (Array.isArray(result)) {
      return {
        markdownReport: 'FMEA drafts generated',
        newFmeaItems: result.map(item => ({
          ...item,
          projectName: item.projectName || projectName,
          epochId: item.epochId || epochId,
          status: item.status || 'draft'
        }))
      };
    }

    if (result.newFmeaItems) {
      result.newFmeaItems = result.newFmeaItems.map((item: any) => ({
        ...item,
        projectName: item.projectName || projectName,
        epochId: item.epochId || epochId,
        status: item.status || 'draft'
      }));
    }

    return result;
  } catch (e) {
    console.error("Backend Error", e);
    throw e;
  }
};

// Helper: Generate FMEA Drafts via Direct AI
const generateFmeaDraftsDirect = async (
  fmeaStandard: FmeaStandard,
  pDiagram: PDiagramData,
  customerRequirements: any[],
  availableFunctions: string[],
  existingFmea: FmeaItem[],
  globalKnowledge: any[],
  existingControls: Control[],
  existingDvpr: DvprTest[],
  maxItems: number
): Promise<{ markdownReport: string, newFmeaItems: Partial<FmeaItem>[] }> => {
  if (!ai || !apiKey) {
    throw new Error("Gemini API Key is missing.");
  }

  try {
    const selectedItemSchema = FMEA_SCHEMAS[fmeaStandard as string] || FMEA_SCHEMAS['AIAG_VDA'];
    const responseSchema = getFmeaGenerationResponseSchema(selectedItemSchema);

    const prompt = getFmeaGenerationPrompt(
      fmeaStandard,
      pDiagram,
      customerRequirements,
      availableFunctions,
      existingFmea,
      globalKnowledge,
      existingControls,
      existingDvpr,
      maxItems
    );

    console.log(`🎯 Generating FMEA items using ${fmeaStandard} standard`);

    const response = await ai.models.generateContent({
      model: "gemini-3-flash-preview",
      contents: [{ role: "user", parts: [{ text: prompt }] }],
      config: {
        responseMimeType: "application/json",
        responseSchema: responseSchema,
        temperature: 0.4,
      },
    });

    const text = response.text;
    if (!text) throw new Error("No response from AI.");

    return cleanJson(text);

  } catch (error: any) {
    console.error("FMEA Gen Error:", error);
    throw error;
  }
};

export const generateFmeaDrafts = async (
  pDiagram: PDiagramData,
  existingFmea: FmeaItem[],
  existingControls: Control[],
  existingDvpr: DvprTest[],
  availableFunctions: string[],
  customerRequirements: any[] = [],
  maxItems: number = 20,
  globalKnowledge: any[] = [],
  options?: {
    apiMode?: 'direct' | 'backend',
    projectName?: string,
    epochId?: number,
    fmeaStandard?: FmeaStandard
  }
): Promise<{ markdownReport: string, newFmeaItems: Partial<FmeaItem>[] }> => {
  const mode = options?.apiMode || 'backend'; // Default to backend
  const projectName = options?.projectName || 'Unknown Project';
  const epochId = options?.epochId || 1;

  const fmeaStandardRaw = options?.fmeaStandard || 'AIAG_VDA';
  const fmeaStandard = typeof fmeaStandardRaw === 'string'
    ? fmeaStandardRaw
    : (fmeaStandardRaw as any).name || (fmeaStandardRaw as any).standard || 'AIAG_VDA';

  if (mode === 'backend') {
    const payload = {
      p_diagram: pDiagram,
      existing_fmea: existingFmea,
      existing_controls: existingControls,
      existing_dvpr: existingDvpr,
      available_functions: availableFunctions,
      customer_requirements: customerRequirements,
      max_items: maxItems,
      global_knowledge: globalKnowledge,
      project_name: projectName,
      epoch_id: epochId,
      fmea_standard: fmeaStandard
    };
    return generateFmeaDraftsBackend(payload, projectName, epochId);
  }

  return generateFmeaDraftsDirect(
    fmeaStandard as FmeaStandard,
    pDiagram,
    customerRequirements,
    availableFunctions,
    existingFmea,
    globalKnowledge,
    existingControls,
    existingDvpr,
    maxItems
  );
};

export const generateFmeaInsight = async (
  context: string,
  options?: { apiMode?: 'direct' | 'backend' }
): Promise<AiInsight> => {
  const mode = options?.apiMode || 'backend';

  if (mode === 'backend') {
    try {
      return await authenticatedFetchJSON('/api/agents/fmea/insight', {
        method: 'POST',
        body: JSON.stringify({ context })
      });
    } catch (e) {
      console.error("Backend Insight Error", e);
      throw e;
    }
  }

  if (!ai || !apiKey) {
    return { type: 'summary', content: "API Key Missing" };
  }

  try {
    const prompt = `
        You are an expert FMEA and Reliability Engineer.
        Analyze the following data snippet and provide a brief, actionable insight or summary.
        If it's a list of risks, identify the highest risk.
        If it's general data, summarize the key takeaway.
        
        Data Context:
        ${context}
        
        Keep the response under 50 words.
      `;

    const response = await ai.models.generateContent({
      model: "gemini-3-flash-preview",
      contents: [{ role: "user", parts: [{ text: prompt }] }],
    });

    const text = response.text || "No insight generated.";
    return { type: 'summary', content: text };

  } catch (e: any) {
    console.error("Insight Error", e);
    return { type: 'summary', content: "Error generating insight." };
  }
};

export const generateProjectReport = async (
  dvprItems: DvprItem[],
  altData: AltDataPoint[],
  options?: { apiMode?: 'direct' | 'backend' }
): Promise<string> => {
  const mode = options?.apiMode || 'backend';

  if (mode === 'backend') {
    try {
      return await authenticatedFetchJSON('/api/agents/project/report', {
        method: 'POST',
        body: JSON.stringify({
          dvpr_items: dvprItems,
          alt_data: altData
        })
      });
    } catch (e) {
      console.error("Backend Report Error", e);
      throw e;
    }
  }

  if (!ai || !apiKey) {
    return "Gemini API Key is missing. Cannot generate report.";
  }

  try {
    const prompt = `
        You are a Senior Reliability Engineer. 
        Generate a concise executive summary report based on the following DVP&R (Design Verification Plan & Report) and ALT (Accelerated Life Test) data.

        DVP&R Data:
        ${JSON.stringify(dvprItems.map(i => ({ test: i.testMethod, status: i.status, failures: i.failures })))}

        ALT Data Summary:
        ${JSON.stringify(altData.slice(0, 5))} (Sample of data points)

        Report Structure:
        1. **Executive Summary**: High-level status (Green/Yellow/Red).
        2. **Key Risks**: Mention any failures in DVP&R.
        3. **Reliability Estimate**: Comment on the ALT data trends (increasing/decreasing reliability).
        4. **Recommendations**: Next steps.

        Format in Markdown.
      `;

    const response = await ai.models.generateContent({
      model: "gemini-3-flash-preview",
      contents: [{ role: "user", parts: [{ text: prompt }] }],
    });

    return response.text || "Report generation failed.";

  } catch (error: any) {
    console.error("Report Gen Error:", error);
    return `Error generating report: ${error.message}`;
  }
};

// ============================================================================
// NEW AGENT FUNCTIONS: Proper DfR Workflow
// ============================================================================

const functionalArchitectureSchema: Schema = {
  type: Type.OBJECT,
  properties: {
    systemFunctions: {
      type: Type.ARRAY,
      items: {
        type: Type.OBJECT,
        properties: {
          functionName: { type: Type.STRING },
          functionId: { type: Type.STRING, description: "slug-like ID (e.g., 'fluid-aeration')" },
          description: { type: Type.STRING, description: "Technical description of the function" },
          relatedRequirements: { type: Type.ARRAY, items: { type: Type.STRING }, description: "IDs of related customer requirements" },
          inputs: { type: Type.ARRAY, items: { type: Type.STRING } },
          outputs: { type: Type.ARRAY, items: { type: Type.STRING } }
        },
        required: ["functionName", "functionId", "description", "relatedRequirements"]
      }
    }
  },
  required: ["systemFunctions"]
};

const optimizationSchema: Schema = {
  type: Type.OBJECT,
  properties: {
    optimizations: {
      type: Type.ARRAY,
      items: {
        type: Type.OBJECT,
        properties: {
          targetFmeaId: { type: Type.STRING },
          proposedAction: { type: Type.STRING, description: "Specific design or control change proposed" },
          actionType: { type: Type.STRING, description: "Prevention Control, Detection Control, or Design Change" },
          rationale: { type: Type.STRING },
          expectedNewS: { type: Type.NUMBER },
          expectedNewO: { type: Type.NUMBER },
          expectedNewD: { type: Type.NUMBER }
        },
        required: ["targetFmeaId", "proposedAction", "actionType", "rationale", "expectedNewS", "expectedNewO", "expectedNewD"]
      }
    }
  },
  required: ["optimizations"]
};

/**
 * PHASE 2: Derive System Functions from Customer Requirements
 * Agent: Functional Analysis Agent
 * 
 * **Metadata Note**: This function returns ANALYSIS DATA (not persisted).
 * If you plan to SAVE these functions, add metadata before persistence:
 * - projectName: string
 * - epochId: number  
 * - status: 'draft' | 'active'
 * 
 * Example:
 * ```typescript
 * const functions = await deriveSystemFunctions(requirements);
 * const functionsWithMetadata = functions.map(fn => ({
 *   ...fn,
 *   projectName: activeProject,
 *   epochId: latestEpoch,
 *   status: 'draft'
 * }));
 * ```
 */
export const deriveSystemFunctions = async (
  requirements: any[],
  options?: {
    apiMode?: 'direct' | 'backend',
    projectName?: string,  // For future persistence
    epochId?: number  // For future persistence
  }
): Promise<any[]> => {
  console.log("🔍 deriveSystemFunctions called with:", requirements.length, "requirements");
  if (!requirements || requirements.length === 0) {
    console.warn("⚠️ No requirements provided to deriveSystemFunctions. Returning empty list.");
    return [];
  }
  const mode = options?.apiMode || 'backend';
  console.log("🔍 deriveSystemFunctions mode:", mode);

  if (mode === 'backend') {
    try {
      return await authenticatedFetchJSON('/api/agents/functional/derive', {
        method: 'POST',
        body: JSON.stringify({ requirements })
      });
    } catch (e) {
      console.error("Backend Error", e);
      throw e;
    }
  }

  if (!ai || !apiKey) throw new Error("API Key Missing");

  try {
    const prompt = `
You are a Systems Architect specializing in Functional Analysis.
Analyze the following Customer Requirements and derive the CORE SYSTEM FUNCTIONS needed to satisfy them.

Customer Requirements:
${JSON.stringify(requirements, null, 2)}

Instructions:
1. For each requirement, identify what FUNCTION the system must perform to meet it
2. Group related requirements under consolidated functional blocks
3. Create a hierarchical function tree if needed
4. Assign unique function IDs (e.g., "fluid-aeration", "power-delivery")
5. Link each function to the requirement IDs it satisfies

Example:
- CR-001 "Aerate milk to 20% DO" → Function: "Fluid Aeration" (fluid-aeration)
- CR-002 "Operate quietly" → Function: "Acoustic Damping" (acoustic-damping)
- CR-003 "No leaks" → Function: "Fluid Containment" (fluid-containment)

Be precise and technical. Return a structured list of functions.
        `;

    const response = await ai.models.generateContent({
      model: "gemini-3-flash-preview",
      contents: [{ role: "user", parts: [{ text: prompt }] }],
      config: {
        responseMimeType: "application/json",
        responseSchema: functionalArchitectureSchema,
        temperature: 0.3,
      },
    });

    const text = response.text;
    if (!text) throw new Error("No response from AI");

    return cleanJson(text).systemFunctions;

  } catch (e: any) {
    console.error("Function Derivation Error", e);
    throw e;
  }
};

/**
 * PHASE 5: Generate Design Optimizations to Reduce RPN
 * Agents: Function Analysis Agent + Physical Development Agent
 * 
 * **Metadata Note**: Returns OPTIMIZATION RECOMMENDATIONS (analysis data).
 * If you convert these to ACTIONS and persist them, add metadata:
 * - projectName: string
 * - epochId: number
 * - status: 'planned' | 'in-progress' | 'completed'
 * 
 * Example:
 * ```typescript
 * const optimizations = await generateDesignOptimizations(fmeaItems);
 * const actions = optimizations.map(opt => ({
 *   ...opt,
 *   projectName: activeProject,
 *   epochId: latestEpoch,
 *   status: 'planned',
 *   actionType: 'optimization'
 * }));
 * ```
 */
export const generateDesignOptimizations = async (
  fmeaItems: FmeaItem[],
  pDiagramContext?: PDiagramData,
  customerRequirements?: any[],
  options?: {
    apiMode?: 'direct' | 'backend',
    projectName?: string,  // For future action persistence
    epochId?: number  // For future action persistence
  }
): Promise<any[]> => {
  const mode = options?.apiMode || 'backend';

  if (mode === 'backend') {
    try {
      const payload = {
        fmea_items: fmeaItems,
        p_diagram_context: pDiagramContext,
        customer_requirements: customerRequirements
      };
      return await authenticatedFetchJSON('/api/agents/optimization/generate', {
        method: 'POST',
        body: JSON.stringify(payload)
      });
    } catch (e) {
      console.error("Backend Error", e);
      throw e;
    }
  }

  if (!ai || !apiKey) throw new Error("API Key Missing");

  try {
    const highRiskItems = fmeaItems
      .map(item => ({ ...item, RPN: (item.S || 1) * (item.O || 1) * (item.D || 1) }))
      .sort((a, b) => (b.RPN || 0) - (a.RPN || 0))
      .slice(0, 10); // Focus on top 10 risk items

    const prompt = `
You are a dual-mode AI agent combining:
1. **Function Analysis Agent**: Proposes functional architecture changes
2. **Physical Development Agent**: Proposes design and control improvements

Review these high-risk FMEA items and propose CONCRETE optimizations to reduce RPN.

Context:
- P-Diagram: ${JSON.stringify(pDiagramContext || {}, null, 2)}
- Customer Requirements: ${JSON.stringify(customerRequirements || [], null, 2)}

High Risk Items:
${JSON.stringify(highRiskItems.map(i => ({
      id: i.id,
      failureMode: i.failureMode,
      effects: i.effects,
      S: i.S,
      O: i.O,
      D: i.D,
      RPN: i.RPN,
      currentControls: {
        prevention: i.preventionControls,
        detection: i.detectionControls
      }
    })), null, 2)}

For each high-risk item, propose ONE specific optimization:

**Function Analysis Agent** should propose:
- Function decomposition (split complex functions)
- Function consolidation (merge redundant functions)
- Alternative functional architectures

**Physical Development Agent** should propose:
- Material changes (e.g., "ceramic bearings instead of steel")
- Design changes (e.g., "dual seal with leak sensor")
- New prevention controls
- New detection controls

For each proposal:
1. Specify the target FMEA ID
2. Describe the CONCRETE action
3. Classify as "Prevention Control", "Detection Control", or "Design Change"
4. Provide rationale linked to P-Diagram noise factors or customer requirements
5. Estimate the NEW S, O, D values after implementation

Prioritize:
- Critical safety items (S ≥ 8)
- Items violating customer requirements
- Items with highest RPN

Return structured optimization proposals.
         `;

    const response = await ai.models.generateContent({
      model: "gemini-3-flash-preview",
      contents: [{ role: "user", parts: [{ text: prompt }] }],
      config: {
        responseMimeType: "application/json",
        responseSchema: optimizationSchema,
        temperature: 0.4,
      },
    });

    const text = response.text;
    if (!text) return [];
    return cleanJson(text).optimizations;

  } catch (e: any) {
    console.error("Optimization Error", e);
    return [];
  }
};

// ============================================================================
// RESEARCH AGENT: Web Search & Competitive Intelligence
// ============================================================================

/**
 * PHASE 0: Conduct Market Research
 * Searches web for competitive products, failure modes, and best practices
 */
export const conductMarketResearch = async (
  productCategory: string,
  customerRequirements: any[],
  options?: { apiMode?: 'direct' | 'backend' }
): Promise<any> => {
  const mode = options?.apiMode || (getEnv('VITE_AI_MODE') === 'direct' ? 'direct' : 'backend');

  if (mode === 'backend') {
    try {
      return await authenticatedFetchJSON('/api/agents/research/market', {
        method: 'POST',
        body: JSON.stringify({ product_category: productCategory, requirements: customerRequirements })
      });
    } catch (e) {
      console.error("Backend Error", e);
      throw e;
    }
  }

  if (!ai || !apiKey) throw new Error("API Key Missing");

  try {
    const requirementsText = customerRequirements
      .map(r => `${r.id}: ${r.requirementText}`)
      .join('\n');

    const prompt = `
You are a Market Research Analyst specializing in product reliability and competitive intelligence.

Conduct comprehensive research on: "${productCategory}"

Customer Requirements Context:
${requirementsText}

Research Tasks:
1. **Competitive Products**: Identify 5-10 similar products currently on the market
   - Extract: Product name, manufacturer, price range, key features
   - Note: Motor specs, materials, design approach

2. **Common Failure Modes**: Based on customer reviews and product reports
   - What are the most frequent failures in this product category?
   - What is the typical time-to-failure?
   - What are the root causes?

3. **Best Practices**: From industry knowledge
   - What design features correlate with higher reliability?
   - What materials are proven for this application?
   - What are common design mistakes to avoid?

4. **Innovation Opportunities**: Gaps in current market
   - What customer complaints are NOT being addressed?
   - What features are missing from current products?
   - Where can we differentiate?

5. **Safety & Standards**: Regulatory requirements
   - What safety standards apply (UL, IEC, ISO)?
   - What testing is required?
   - What are common compliance issues?

Be detailed and technical. Cite specific product examples where possible.
Return structured JSON with all findings.
    `;

    // Use Gemini with Google Search grounding
    const response = await ai.models.generateContent({
      model: "gemini-3-flash-preview",
      contents: [{ role: "user", parts: [{ text: prompt }] }],
      config: {
        temperature: 0.3,
        // Enable Google Search for real-time data
        tools: [{ googleSearch: {} }]
      }
    });

    const text = response.text || "{}";

    // Parse and structure the response
    try {
      const data = cleanJson(text);
      return data;
    } catch {
      // If not valid JSON, create structured response from text
      return {
        competitiveProducts: [],
        failureModes: [],
        bestPractices: extractListFromText(text, "Best Practices"),
        innovationOpportunities: extractListFromText(text, "Innovation"),
        standards: extractListFromText(text, "Standards"),
        rawResearch: text
      };
    }

  } catch (e: unknown) {
    console.error("Research Error", e);
    const message = e instanceof Error ? e.message : String(e);
    return {
      error: message,
      competitiveProducts: [],
      failureModes: [],
      bestPractices: [],
      innovationOpportunities: [],
      standards: []
    };
  }
};

/**
 * Helper: Extract list items from unstructured text
 */
function extractListFromText(text: string, section: string): string[] {
  const lines = text.split('\n');
  const items: string[] = [];
  let inSection = false;

  for (const line of lines) {
    if (line.toLowerCase().includes(section.toLowerCase())) {
      inSection = true;
      continue;
    }
    if (inSection && (line.startsWith('-') || line.startsWith('*') || line.match(/^\d+\./))) {
      items.push(line.replace(/^[-*\d.]\s*/, '').trim());
    }
    if (inSection && line.trim() === '') {
      break;
    }
  }

  return items.filter(i => i.length > 0);
}

/**
 * Search for similar products in knowledge base
 */
export const findSimilarProducts = async (
  requirements: any[],
  existingProducts: any[]
): Promise<any[]> => {
  if (!ai || !apiKey) return [];

  try {
    const prompt = `
You are a product similarity analyst.

New Product Requirements:
${JSON.stringify(requirements, null, 2)}

Existing Products in Database:
${JSON.stringify(existingProducts.slice(0, 10), null, 2)}

Task:
Identify which existing products are MOST SIMILAR to the new requirements.
Rank by similarity (0-100%).
Return top 5 matches with similarity scores and reasoning.

Return JSON: 
{
  "matches": [
    {
      "productId": "...",
      "productName": "...",
      "similarityScore": 85,
      "reasoning": "Both are fluid mixing devices with similar power requirements"
    }
  ]
}
    `;

    const response = await ai.models.generateContent({
      model: "gemini-3-flash-preview",
      contents: [{ role: "user", parts: [{ text: prompt }] }],
      config: {
        responseMimeType: "application/json",
        temperature: 0.2
      }
    });

    const text = response.text;
    if (!text) return [];

    const data = JSON.parse(text);
    return data.matches || [];

  } catch (e: any) {
    console.error("Similar Product Search Error", e);
    return [];
  }
};
